
import { useEffect, useState } from 'react';
import { Button, Divider, Space, Table, Tag, Typography, Form, DatePicker, Row, Col, Select, message, Checkbox } from 'antd';
import { DownloadOutlined, } from '@ant-design/icons';
import { fetchTransactionsReportData, getTransactionReportCsv } from '../../_services/reports.service';
import Cupoints from "../../Components/Cupoints";
import moment from "moment";


const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterForm = ({ initialValue, hasData, onSearch, onExport, exporting }) => {
    const [form] = Form.useForm();

    const exportData = () => {
        const values = form.getFieldsValue();
        onExport(values);
    };

    const onFormValuesChange = (changedValues, allValues) => {
        onSearch(allValues)
    }

    return (
        <Form form={form} initialValues={initialValue} layout="inline" onValuesChange={onFormValuesChange} style={{ marginBottom: 24 }}>
            <Row style={{ width: '100%' }} justify="space-between" align="middle">
                {/* Left side: date inputs */}
                <Col>
                    <Space>
                        <Form.Item name="range">
                            <RangePicker
                                allowClear={false}
                                placeholder={['Start Date', 'End Date']}
                            />
                        </Form.Item>
                        <Form.Item name="transactionType" label="Transaction Type">
                            <Select
                                placeholder="Select Transaction Type"
                                style={{ width: 100 }}
                                defaultValue=""
                            >
                                <Option value="">Any</Option>
                                <Option value="purchase">Purchase</Option>
                                <Option value="topup">Topup</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="excludeSignupBonus" valuePropName="checked">
                            <Checkbox>Exclude Signup Bonus</Checkbox>
                        </Form.Item>
                    </Space>
                </Col>

                {/* Right side: buttons */}
                <Col>
                    <Space>
                        <Form.Item>
                            <Button icon={<DownloadOutlined />} onClick={exportData} disabled={!hasData} loading={exporting}>
                                Export to CSV
                            </Button>
                        </Form.Item>
                    </Space>
                </Col>
            </Row>
        </Form>
    );
};

const ReportTransactions = () => {

    const [loading, setLoading] = useState(false);
    const [hasData, setHasData] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState();
    const [data, setData] = useState([]);
    const [formValue, setFormValue] = useState({
        range: [moment().startOf("month"), moment().endOf("month")],
        transactionType: '',
        excludeSignupBonus: true,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
    });

    const columns = [
        {
            title: 'Transaction ID',
            dataIndex: 'id',
            render: (_, { id }) => (
                <>
                    <Space direction='vertical' size={0}>
                        <span>{id}</span>
                    </Space>
                </>
            )
        },
        {
            title: 'Order ID',
            dataIndex: 'orderId',
            render: (_, { orderId }) => (
                <>
                    <Space direction='vertical' size={0}>
                        <span>{orderId}</span>
                    </Space>
                </>
            )
        }, {
            title: 'Memo',
            dataIndex: 'memo',
            render: (_, { memo }) => (
                <>
                    <Space direction='vertical' size={0}>
                        <span>{memo}</span>
                    </Space>
                </>
            )
        }, {
            title: 'Amount',
            dataIndex: 'total_cupoints',
            render: (_, { totalCupoints }) => (
                <>
                    <Cupoints points={totalCupoints}></Cupoints>
                </>
            )
        }, {
            title: 'Transaction Type',
            dataIndex: 'transactionType',
            render: (_, { transactionType }) => (
                <>
                    <Tag color={transactionType === "topup" ? "blue" : "orange"}>{transactionType === "topup" ? "Topup" : "Purchase"}</Tag>
                </>
            )
        },
        {
            title: 'Authorizer',
            dataIndex: 'user',
            render: (_, { authorizer }) => (
                <>
                    <Space direction='vertical' size={0}>
                        <span>
                            {authorizer.firstName} {authorizer.lastName} ({authorizer.id})
                        </span>
                        <Text type="secondary" >
                            {authorizer.username}
                        </Text>
                    </Space>
                </>
            )
        },
        {
            title: 'Transaction Date',
            dataIndex: 'date',
            render: (_, { date }) => (
                <>
                    {moment(date).format("L")}
                </>
            )
        },
    ];

    const fetchData = ({ current, pageSize }) => {
        setLoading(true);
        setError(null);

        fetchTransactionsReportData(formValue, {
            page: current,
            pageSize,
        }).then(response => {
            setData(response.data);
            setHasData(response.data.length > 0)
            setLoading(false);
            setPagination((prev) => ({
                ...prev,
                current,
                pageSize,
                total: response.total,
            }));
        }).catch(error => {
            setError("Something went wrong when fetch data!");
            setHasData(false);
            setLoading(false);
        })
    }

    const handleTableChange = (newPagination) => {
        fetchData({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        });
    };

    const exportToCsv = (data) => {
        setExporting(true);
        getTransactionReportCsv(data).then(() => {
            message.success("Report Downloaded!");
            setExporting(false);
        }).catch(error => {
            message.error("Something went wrong when export data!");
            setExporting(false);
        })
    }

    useEffect(() => {
        fetchData({
            current: pagination.current,
            pageSize: pagination.pageSize,
        });
    }, [formValue]);

    return <div>
        <h2>
            Transactions Report
        </h2>
        <Divider />
        <FilterForm hasData={hasData} initialValue={formValue}
            exporting={exporting}
            onSearch={(formData) => { setFormValue(formData) }}
            onExport={(formData) => { exportToCsv(formData) }} />

        <Table
            columns={columns}
            size="middle"
            rowKey={record => record.id}
            dataSource={data}
            locale={{
                emptyText: error || 'No Data found'
            }}
            loading={loading}
            pagination={{
                ...pagination,
                showTotal: (total) => `Total ${total} records`,
            }}
            onChange={handleTableChange}
        />
    </div>
}

export default ReportTransactions;